<template>
  <div style="max-height: 100%">
    <div class="header">
      <div class="list-title">
        <EditorModuleTabs v-if="type === 'editor'" />
        <span v-else>{{ productListText }}</span>
      </div>
      <b-loading
        :is-full-page="true"
        :active.sync="isLoading"
        :can-cancel="false"
      >
        <div class="loading-wrap">
          <div class="loading-icon"></div>
          <p class="loading-text">{{ loadingStateText || $t('loading') }}</p>
        </div>
      </b-loading>
      <div class="right-section">
        <div class="status-filter">
          <b-dropdown
            :value="statusFilter"
            multiple
            aria-role="list"
            position="is-bottom-left"
            @change="onStatusFilterChange"
          >
            <b-button
              slot="trigger"
              icon-left="filter"
              :type="statusFilter.length > 0 ? 'is-info' : ''"
              style="height: 100%"
            >
              {{ $t('template_list.status_filter_label') }}
            </b-button>
            <b-dropdown-item
              v-for="option in availableStatusOptions"
              :key="option.value"
              :value="option.value"
              aria-role="listitem"
            >
              <span
                class="dij-status-badge"
                :class="`dij-status-${option.value
                  .toLowerCase()
                  .replace(' ', '-')}`"
              >
                {{ option.label }}
              </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <div v-if="isLocationFilterAvailable" class="filter-location">
          <filter-location
            :locations="selectedLocations"
            @update="updateLocation"
          >
          </filter-location>
        </div>
        <b-tooltip
          :label="
            showFavorites
              ? $t('template_list.filter_favorites_active')
              : $t('template_list.filter_favorites')
          "
          position="is-bottom"
          :triggers="['hover']"
        >
          <span
            class="favorite-filter-icon"
            :class="{ active: showFavorites }"
            @click="toggleFavoritesFilter"
          >
            <b-icon icon="star" />
          </span>
        </b-tooltip>

        <div class="search">
          <form v-on:submit.prevent="getJobs">
            <b-field>
              <b-input
                :placeholder="$t('search')"
                type="search"
                icon="magnify"
                icon-clickable
                @icon-click="getJobs"
                class="search-input"
                v-model="search"
                ref="searchInput"
                @keydown.native.enter="hideKeyboard"
              >
              </b-input>
            </b-field>
          </form>
        </div>

        <div class="button-wrapper">
          <b-button
            v-if="type !== 'editor'"
            class="flex-fill"
            icon-left="qrcode-scan"
            size="is-large"
            @click="() => (isQRCodeReaderVisible = true)"
          />
        </div>

        <button
          v-if="showAddProductButton"
          @click="handleAddProductClick"
          class="btn-primary"
          data-testid="add-product-btn"
          :disabled="isAddProductButtonDisabled"
        >
          {{ type === 'editor' ? $t('add_template') : $t('add_project') }}
        </button>
        <template v-if="isDeletedJobsAvailable">
          <b-button
            :disabled="computedIsOffline"
            type="is-success"
            size="is-large"
            icon-left="recycle"
            class="dij-jobs-recycle-bin"
            @click="showDeletedJobs"
          />
          <b-modal
            :active="isDeletedJobsVisible"
            has-modal-card
            trap-focus
            class="dij-jobs-deleted-modal"
            @close="hideDeletedJobs"
          >
            <div class="modal-card">
              <header class="modal-card-head">
                <b-icon icon="recycle" size="is-large" />
                <p class="modal-card-title">{{ $t('recycle_bin') }}</p>
              </header>
              <section class="modal-card-body">
                <template v-if="deletedJobs.length > 0">
                  <b-table :data="deletedJobs">
                    <template slot-scope="props">
                      <b-table-column field="title" :label="$t('title')">
                        {{ props.row.title }}
                      </b-table-column>
                      <b-table-column
                        v-if="type !== 'editor'"
                        field="project_number"
                        :label="$t('project_number')"
                      >
                        {{ props.row.projectInfoText }}
                      </b-table-column>
                      <b-table-column
                        v-if="type === 'editor'"
                        field="document_number"
                        :label="$t('document_number')"
                      >
                        {{ props.row.documentNumber }}
                      </b-table-column>
                      <b-table-column field="owner" :label="$t('owner')">
                        {{ props.row.prodCreatedByUser.fullname }}
                      </b-table-column>
                      <b-table-column
                        field="deletedBy"
                        :label="$t('deleted_by')"
                      >
                        {{ props.row.deletedBy }}
                      </b-table-column>
                      <b-table-column
                        field="deletedAt"
                        :label="$t('deleted_at')"
                      >
                        {{ convertEpochToDate(props.row.deletedAt) }}
                      </b-table-column>
                      <b-table-column :label="$t('actions')">
                        <b-tooltip :label="$t('recover')" position="is-top">
                          <b-button
                            class="dij-recycle-bin-recover"
                            type="is-primary"
                            icon-left="backup-restore"
                            @click="undelete(props.row._id)"
                          />
                        </b-tooltip>
                      </b-table-column>
                    </template>
                  </b-table>
                </template>
                <template v-else>
                  {{ $t('recycle_bin_empty') }}
                </template>
              </section>
            </div>
          </b-modal>
        </template>
      </div>
    </div>

    <div class="items">
      <div class="items-container">
        <b-message
          :active.sync="showNoFiltersMessage"
          :title="$t('template_list.warning')"
          type="is-warning"
          :aria-close-label="$t('close_message')"
          auto-close
          :duration="10000"
          >{{ noFiltersMessage }}
        </b-message>
        <b-table
          ref="tableRef"
          :mobile-cards="isMobile && mobileView"
          :data="updatedTableData"
          :loading="waitingForRead"
          :hoverable="true"
          @click="goToJob"
          paginated
          :pagination-simple="true"
          :per-page="perPage"
          :current-page="currentPage"
          :rounded="true"
          @page-change="onPageChange"
          :aria-next-label="$t('template_list.next_page')"
          :aria-previous-label="$t('template_list.previous_page')"
          :aria-page-label="$t('template_list.page_label')"
          :aria-current-label="$t('template_list.current_page')"
          :row-class="getRowClass"
        >
          <template slot-scope="props">
            <b-table-column
              :class="props.row.completenessClass"
              field="picture"
              width="40"
              searchable
            >
              <template
                v-if="isSearchActive"
                slot="searchable"
                slot-scope="props"
              >
                <b-button
                  :v-if="isSearchActive"
                  @click="clearFilters"
                  size="is-small"
                  icon-left="backspace-reverse"
                >
                  {{ $t('clear_filters') }}
                </b-button>
              </template>
              <div
                class="completeness-inner"
                :style="
                  'height:' +
                  props.row.completenessValueFiltered +
                  '%!important;'
                "
              >
                &nbsp;
              </div>
              <div class="image-container">
                <b-tooltip
                  :label="getToolTipByRow(props.row)"
                  type="is-dark"
                  v-if="type !== 'editor'"
                  position="is-right"
                >
                  <img :src="props.row.picture" class="thumbnail" />
                </b-tooltip>
                <img v-else :src="props.row.picture" class="thumbnail" />
                <div
                  class="image-overlay"
                  v-if="props.row.includesRepairAssessment"
                  :title="$t('job_includes_repair_assessment')"
                >
                  <b-icon icon="hammer-wrench" type="is-info is-light" />
                </div>
                <div
                  v-if="!props.row.isSavingOffline"
                  class="image-overlay-offline"
                  :title="
                    props.row.hasCompleteData
                      ? $t('job_downloaded_locally')
                      : $t('job_has_incomplete_data')
                  "
                >
                  <b-icon
                    v-if="props.row.hasCompleteData"
                    icon="cloud-download"
                    type="is-success"
                    v-show="props.row.offlineSavedIcon"
                  />
                  <b-icon
                    v-else
                    icon="cloud-download"
                    type="is-warning"
                    v-show="props.row.offlineSavedIcon"
                  />
                </div>
                <div
                  v-if="
                    props.row.needsSynchronization && !props.row.isSavingOffline
                  "
                  class="image-overlay-offline"
                  style="margin-left: 25px"
                  :title="$t('template_list.job_needs_sync')"
                >
                  <b-icon
                    icon="cloud-upload"
                    type="is-primary"
                    v-show="props.row.offlineSavedIcon"
                  />
                </div>
                <div
                  v-if="props.row.isSavingOffline"
                  class="image-overlay-offline"
                >
                  <b-icon icon="sync" type="is-info" />
                </div>
              </div>
            </b-table-column>
            <b-table-column
              field="status"
              :label="$t('tasks_header.job_status')"
              style="min-width: 100px"
            >
              <span
                v-if="props.row.status"
                class="dij-status-badge"
                :class="`dij-status-${props.row.status
                  .toLowerCase()
                  .replace(' ', '-')}`"
              >
                {{ props.row.status }}
              </span>
              <span v-else class="dij-status-badge dij-status-draft">
                {{ $t('template_list.status_draft') }}
              </span>
            </b-table-column>
            <b-table-column
              field="product_line"
              :label="$t('product_line')"
              width="90"
              searchable
            >
              <template
                v-if="isSearchActive"
                slot="searchable"
                slot-scope="props"
              >
                <div :v-if="isSearchActive">
                  <b-input
                    v-if="isSearchActive"
                    v-model="filterObject['product_line']"
                    :placeholder="$t('filter')"
                    size="is-small"
                  />
                </div>
              </template>
              {{ props.row.product_line }}
            </b-table-column>
            <b-table-column
              v-for="columnData in columnsData"
              :field="columnData"
              :label="formatColumnName(columnData)"
              :key="columnData"
              searchable
            >
              <div v-if="isSearchActive" slot="searchable" slot-scope="props">
                <div v-if="isSearchActive">
                  <b-input
                    v-if="isSearchActive"
                    v-model="filterObject[props.column.field]"
                    :placeholder="$t('filter')"
                    size="is-small"
                  />
                </div>
              </div>
              <p
                :class="
                  props.row[columnData] && props.row[columnData].length > 40
                    ? 'text-column-limit'
                    : ''
                "
              >
                {{ props.row[columnData] }}
              </p>
            </b-table-column>
            <b-table-column
              v-if="type !== 'editor'"
              field="CSM_Repair_Engineer"
              :label="$t('template_list.csm_repair_engineer')"
              style="min-width: 110px"
              searchable
            >
              <template
                v-if="isSearchActive"
                slot="searchable"
                slot-scope="props"
              >
                <div :v-if="isSearchActive">
                  <b-input
                    v-if="isSearchActive"
                    v-model="filterObject['CSM_Repair_Engineer']"
                    :placeholder="$t('filter')"
                    size="is-small"
                  />
                </div> </template
              >{{ props.row.CSM_Repair_Engineer }}
            </b-table-column>

            <b-table-column
              v-if="type === 'editor'"
              field="sapReference"
              :label="$t('sap_reference')"
              style="min-width: 110px"
              searchable
            >
              <template
                v-if="isSearchActive"
                slot="searchable"
                slot-scope="props"
              >
                <div :v-if="isSearchActive">
                  <b-input
                    v-if="isSearchActive"
                    v-model="filterObject['sapReference']"
                    :placeholder="$t('filter')"
                    size="is-small"
                  />
                </div>
              </template>
              <p
                :class="
                  props.row.sapReference && props.row.sapReference.length > 40
                    ? 'text-column-limit'
                    : ''
                "
              >
                {{ props.row.sapReference }}
              </p>
            </b-table-column>
            <b-table-column
              v-if="type === 'editor'"
              field="date"
              :label="$t('current_owner')"
              style="min-width: 110px"
              searchable
            >
              <template
                v-if="isSearchActive"
                slot="searchable"
                slot-scope="props"
              >
                <div :v-if="isSearchActive">
                  <b-input
                    v-if="isSearchActive"
                    v-model="filterObject['created_by']"
                    :placeholder="$t('filter')"
                    size="is-small"
                  />
                </div> </template
              >{{ props.row.created_by }}
            </b-table-column>
            <b-table-column
              v-if="type === 'editor'"
              field="date"
              :label="$t('date')"
              style="min-width: 110px"
              searchable
            >
              <template
                v-if="isSearchActive"
                slot="searchable"
                slot-scope="props"
              >
                <div :v-if="isSearchActive">
                  <b-input
                    v-if="isSearchActive"
                    v-model="filterObject['date']"
                    :placeholder="$t('filter')"
                    size="is-small"
                  />
                </div>
              </template>

              {{ props.row.date }}</b-table-column
            >
            <b-table-column
              v-else
              field="date"
              :label="$t('current_owner_date')"
              style="min-width: 110px"
              searchable
            >
              <template
                :hidden="!isSearchActive"
                v-if="isSearchActive"
                slot="searchable"
                slot-scope="props"
              >
                <div :v-if="isSearchActive">
                  <b-input
                    v-if="isSearchActive"
                    v-model="filterObject['owner_date']"
                    :placeholder="$t('filter')"
                    size="is-small"
                  />
                </div>
              </template>
              {{ props.row.owner_date }}</b-table-column
            >

            <b-table-column custom-key="actions" class="options-column">
              <div
                @click="
                  addToFavorites(
                    props.row.id,
                    props.row.nameId,
                    props.row.favoriteCount
                  )
                "
              >
                <b-icon icon="star" :class="favoriteStarClass(props.row.id)">
                </b-icon>
                {{ props.row.favoriteCount }}
              </div>
              <div @click="optionsClicked = true">
                <b-dropdown
                  v-if="type === 'editor' || type === 'publisher'"
                  aria-role="list"
                  :position="props.index > 1 ? 'is-top-left' : 'is-bottom-left'"
                >
                  <button class="button options-more-btn" slot="trigger">
                    <b-icon icon="dots-horizontal"></b-icon>
                  </button>

                  <b-dropdown-item
                    :disabled="computedIsOffline || !canEditJob(props.row)"
                    aria-role="listitem"
                    @click="editJob(props.row.id)"
                  >
                    <div class="media">
                      <b-icon class="media-left" icon="pencil"></b-icon>
                      <div class="media-content">
                        <h3>{{ $t('edit') }}</h3>
                      </div>
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item
                    :disabled="computedIsOffline || !canEditJob(props.row)"
                    aria-role="listitem"
                    @click="deleteJob(props.row.id, props.row.title)"
                  >
                    <div class="media">
                      <b-icon class="media-left" icon="delete"></b-icon>
                      <div class="media-content">
                        <h3>{{ $t('delete') }}</h3>
                      </div>
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item
                    :disabled="computedIsOffline || !canEditJob(props.row)"
                    aria-role="listitem"
                    @click="duplicate(props.row.id)"
                  >
                    <div class="media">
                      <b-icon class="media-left" icon="content-copy"></b-icon>
                      <div class="media-content">
                        <h3>{{ $t('duplicate') }}</h3>
                      </div>
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item
                    :disabled="computedIsOffline || !canEditJob(props.row)"
                    aria-role="listitem"
                    @click="copyFrom(props.row.id)"
                  >
                    <div class="media">
                      <b-icon
                        class="media-left"
                        icon="swap-horizontal"
                      ></b-icon>
                      <div class="media-content">
                        <h3>{{ $t('copyfrom') }}</h3>
                      </div>
                    </div>
                  </b-dropdown-item>
                  <!-- <b-dropdown-item
                    :disabled="computedIsOffline"
                    aria-role="listitem"
                    v-if="showSaveOffline(props.row)"
                    @click="saveJobOfflineWrapper(props.row.id)"
                  >
                    <div class="media">
                      <b-icon
                        class="media-left"
                        icon="download-lock-outline"
                      ></b-icon>
                      <div class="media-content">
                        <h3>Save offline</h3>
                      </div>
                    </div>
                  </b-dropdown-item>

                  <b-dropdown-item
                    :disabled="computedIsOffline"
                    aria-role="listitem"
                    v-if="showSyncOnline(props.row)"
                    @click="syncJobOnline(props.row.id)"
                  >
                    <div class="media">
                      <b-icon class="media-left" icon="upload-outline"></b-icon>
                      <div class="media-content">
                        <h3>Sync Job</h3>
                      </div>
                    </div>
                  </b-dropdown-item> -->
                </b-dropdown>
              </div>
            </b-table-column>
          </template>
          <template slot="empty" v-if="!waitingForRead">
            <section class="section">
              <div class="content has-text-grey has-text-centered">
                <p>
                  <b-icon icon="emoticon-sad" size="is-large"> </b-icon>
                </p>
                <p>{{ $t('nothing_here') }}.</p>
              </div>
            </section>
          </template>
        </b-table>
        <!-- <b-button
          v-if="showChangeView"
          @click="() => (mobileView = !mobileView)"
          type="is-info"
          >Change Table View</b-button
        > -->
      </div>
    </div>

    <div class="floating-button" style="display: none">
      <img
        src="@/assets/se_repair.png"
        @click="openChatBox"
        @mouseenter="showTooltip = true"
        @mouseleave="showTooltip = false"
        class="clickable-img"
        alt="Help Icon"
      />
      <div v-show="showTooltip" class="tooltip">
        {{ $t('task_page.need_help_chat') }}
      </div>
    </div>

    <FloatingRepairSelect @open="handleRepairSelect" />

    <QrCodeReaderModal
      v-if="isQRCodeReaderVisible"
      @data="onQRDecode"
      @close="() => (isQRCodeReaderVisible = false)"
    />
  </div>
</template>

<script>
// import debounce from 'lodash/debounce';
import FilterLocation from './FilterLocation';
import FilterStatus from './FilterStatus';
import OfflineMixin from '../../mixins/OfflineMixin';
import QrCodeReaderModal from './QrCodeReaderModal';
import FloatingRepairSelect from '../Shared/FloatingRepairSelect';
import EditorModuleTabs from '../Editor/ModuleTabs';
import WorkOrderListStrategy from '../ChatBox/strategies/WorkOrderListStrategy';
import {
  getDirtySyncRecordsByJobId,
  getRecordsUsingIndex,
} from '../../services/db';
import { SET_LOADING_STATE } from '../../store/loading/mutations/mutationTypes';
import {
  UPDATE_FAVORITE_COUNT,
  SET_FAVOURITE_CURRENT_JOB,
} from '../../store/jobs/mutations/mutationTypes';

import { WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION } from '../../store/websocket/actions/actionTypes';
import {
  JOB_STATUS,
  TEMPLATE_STATUS,
  DEFAULT_STATUS_FILTERS,
  getEffectiveJobStatus,
} from '../../constants/jobStatus';

export default {
  name: 'template-list',
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ['editor', 'publisher', 'report'].includes(value),
    },
    columnsData: {
      type: Array,
      default: () => [],
    },
  },
  mixins: [OfflineMixin],
  data() {
    return {
      isLoading: false,
      loadingStateText: '',
      optionsClicked: false,
      showFavorites: false,
      search: '',
      tableData: [],
      perPage: 10,
      isMobile: false,
      mobileView: false,
      isSearchActive: false,
      filterObject: {},
      updatedTableData: [],
      noFiltersMessage: '',
      showNoFiltersMessage: false,
      cookieFilterChecked: false,
      doneFetching: false,
      isDeletedJobsVisible: false,
      jobsUndeleted: false,
      selectedLocations: [],
      showTooltip: false,
      currentPage: 1,
      jobsFetchingToast: null,
      queryCounter: 0, // Counter to track the number of queries made for search
      lastQueryKey: '',
      isQRCodeReaderVisible: false,
      favoriteSync: {},
      favoriteTimers: {},
      statusFilter: [],
    };
  },
  components: {
    'filter-location': FilterLocation,
    'filter-status': FilterStatus,
    FloatingRepairSelect,
    QrCodeReaderModal,
    EditorModuleTabs,
  },
  computed: {
    showAddProductButton() {
      return this.type === 'editor' || this.type === 'publisher';
    },
    isAddProductButtonDisabled() {
      if (this.computedIsOffline) return true;
      if (
        (this.type === 'editor' || this.type === 'publisher') &&
        !this.canCreateJob
      )
        return true;
      return false;
    },
    canCreateJob() {
      if (this.type !== 'editor' && this.type !== 'publisher') return false;
      if (this.$store.getters.isPowerAdmin) return true;

      return this.$store.getters.hasPermission(
        this.type === 'editor'
          ? 'editor.create_templates'
          : 'publisher.create_jobs'
      );
    },
    productListText() {
      if (this.$route.name.includes('editor')) {
        return this.$t('product_template_list');
      }
      if (this.$route.name.includes('publisher')) {
        return this.$t('project_list');
      }
      return this.$t('work_order_list');
    },
    user() {
      return this.$store.state.currentUser;
    },
    sites() {
      return this.$store.state.sites;
    },
    jobs() {
      return this.$store.state.jobs;
    },
    showChangeView() {
      if (
        this.$route.name.includes('inspector') &&
        navigator.maxTouchPoints > 0 &&
        window.orientation < 90
      ) {
        return true;
      }
      return false;
    },
    isMobileDevice() {
      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        )
      ) {
        // true for mobile
        return true;
      }
      // false for not mobile device
      return false;
    },
    deletedJobs() {
      return this.$store.state.deletedJobs;
    },
    isLocationFilterAvailable() {
      const isSuperAdmin = parseInt(this.user.privil, 10) === 1;
      return (
        this.type !== 'editor' &&
        (isSuperAdmin ||
          (this.user.secondaryLocations &&
            this.user.secondaryLocations.length > 0))
      );
    },
    isDeletedJobsAvailable() {
      return (this.type === 'editor' || this.type === 'publisher') && this.user;
    },
    waitingForRead() {
      return this.$store.state.waitingForRead;
    },
    availableStatusOptions() {
      if (this.type === 'editor') {
        return [
          {
            value: TEMPLATE_STATUS.DRAFT,
            label: this.$t('template_list.status_draft'),
          },
          {
            value: TEMPLATE_STATUS.ACTIVE,
            label: this.$t('template_list.status_active'),
          },
        ];
      }
      return [
        {
          value: JOB_STATUS.DRAFT,
          label: this.$t('template_list.status_draft'),
        },
        {
          value: JOB_STATUS.IN_PROGRESS,
          label: this.$t('template_list.status_in_progress'),
        },
        {
          value: JOB_STATUS.CLOSED,
          label: this.$t('template_list.status_closed'),
        },
      ];
    },
  },

  async created() {
    if (!this.sites || this.sites.length === 0) {
      await this.$store.dispatch('getSites');
    }
    this.$store.dispatch('clearCurrentJob');
    this.$store.dispatch('setCurrentTasks', {});
    this.checkSearchCookie();

    this.$store.dispatch(WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION, {
      action: 'job:on_taken_offline',
      callback: this.rePrepareJob,
    });
    this.$store.dispatch(WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION, {
      action: 'job:on_sync_online',
      callback: this.rePrepareJob,
    });
  },

  async beforeMount() {
    window.addEventListener('online', this.refreshPageOnOnline);
  },

  async beforeDestroy() {
    this._listCancelled = true;
    this.$store.commit('CANCEL_PROGRESS_TASK', { key: 'list-loading' });
    this.closeJobFetchingToast();
    window.removeEventListener('online', this.refreshPageOnOnline);
  },

  async mounted() {
    // Check previous type from localStorage and clear search if needed
    const prevType = localStorage.getItem('templateListPrevType');
    const nonEditorTypes = ['publisher', 'inspector', 'report'];
    // Clear search if switching between editor <-> non-editor types
    if (
      (this.type === 'editor' && nonEditorTypes.includes(prevType)) ||
      (nonEditorTypes.includes(this.type) && prevType === 'editor')
    ) {
      this.search = '';
      sessionStorage.removeItem('search');
      this.selectedLocations = [];
      this.$cookies.remove('locations');
    }
    // Save current type for next mount
    localStorage.setItem('templateListPrevType', this.type);
    // Visual aid in case there are more items that shown on the list's total
    this.$nextTick(() => {
      const container = document.querySelector('nav.pagination');
      if (container) {
        const moreElement = document.createElement('small');
        moreElement.classList.add('info');
        moreElement.classList.add('more');
        container.appendChild(moreElement);
      }
    });
    this.checkSearchCookie();
    this.checkFavoriteCookie();

    if (this.type !== 'editor') {
      const savedLocations = this.$cookies.get('locations');
      if (savedLocations) {
        this.selectedLocations = savedLocations;
      } else {
        this.selectedLocations = [];
      }
    }

    const savedStatusFilter = this.$cookies.get(
      `dij_status_filter_${this.type}`
    );
    if (savedStatusFilter !== null && savedStatusFilter !== undefined) {
      this.statusFilter = Array.isArray(savedStatusFilter)
        ? savedStatusFilter
        : [savedStatusFilter].filter(Boolean);
    } else {
      this.statusFilter = [...(DEFAULT_STATUS_FILTERS[this.type] || [])];
    }

    await this.getJobs();
    const myself = this;
    if (typeof window.orientation !== 'undefined' && window.orientation === 0) {
      this.isMobile = true;
    }
    window.addEventListener('orientationchange', (event) => {
      const orientation = event.target.screen.orientation.angle;
      if (orientation === 0) {
        myself.isMobile = true;

        return true;
      }
      myself.isMobile = false;
    });

    await this.checkFilterCookie();

    if (this.isDeletedJobsAvailable) {
      this.$store.dispatch('getDeletedJobs', { type: this.type });
    }
    this.isLoading = false;
  },

  watch: {
    isLoading(isLoading) {
      if (!isLoading) {
        this.loadingStateText = null;
      }
    },
    async isDeletedJobsVisible(val) {
      if (!val && this.jobsUndeleted) {
        await this.getJobs(true);
      }
    },
    async computedIsOffline(isOffline) {
      if (!isOffline) {
        this.refreshPageOnOnline();
      }
    },
    search: {
      handler(newValue) {
        // eslint-disable-next-line eqeqeq
        if (newValue == '') {
          sessionStorage.removeItem('search');

          this.currentPage = 1;
          return this.getJobs();
        }
      },
    },
    tableData: {
      deep: true,
      async handler(value) {
        const uniqueJobs = Array.from(
          new Map(value.map((job) => [job.id, job])).values()
        );
        uniqueJobs.sort((a, b) => new Date(b.date) - new Date(a.date));

        if (Object.keys(this.filterObject).length > 0) {
          if (!this.cookieFilterChecked) await this.checkFilterCookie();

          this.updatedTableData = uniqueJobs.filter((row) =>
            this.filterFunc(row)
          );

          if (
            this.doneFetching &&
            this.jobs.length > 0 &&
            this.updatedTableData.length === 0
          ) {
            this.showNoFiltersMessage = true;
            this.noFiltersMessage = this.$t(
              'template_list.no_results_clear_filters'
            );
          }
        } else {
          this.updatedTableData = uniqueJobs;
        }
      },
    },
    filterObject: {
      handler(newVal) {
        const keys = Object.keys(newVal);
        if (keys.length > 0) {
          const objectEntries = Object.entries(newVal);
          if (!objectEntries.some((key_value) => key_value[1] !== '')) {
            this.filterObject = {};
            this.$cookies.remove('templates_filter');
          } else {
            this.updatedTableData = this.tableData.filter((row) =>
              this.filterFunc(row)
            );

            this.$cookies.set('templates_filter', newVal, 60 * 60 * 2);
          }
        } else {
          this.$cookies.remove('templates_filter');
          this.updatedTableData = this.tableData;
        }
      },
      deep: true,
    },
  },
  methods: {
    getListLoadingText(processedCount = null, total = null) {
      const listName = this.productListText;

      if (processedCount !== null && total !== null) {
        return this.$t('template_list.loading_list_progress', {
          listName,
          processedCount,
          total,
        });
      }

      return this.$t('template_list.loading_list', { listName });
    },
    getListLoadedText() {
      return this.$t('template_list.loaded_list', {
        listName: this.productListText,
      });
    },
    handleRepairSelect() {
      const strategy = new WorkOrderListStrategy();

      this.$chatbox.open({
        title: this.$t('template_list.repair_select_assistant'),
        message: strategy.getInitialMessage(),
        strategy,
        metadata: {
          type: this.type,
          user: this.user,
        },
      });
    },
    buildQueryKey(q) {
      const norm = {
        type: q.type,
        search: (q.search || [])
          .map((s) => String(s).trim())
          .filter(Boolean)
          .sort(),
        favorite: !!q.favorite,
        locations: (q.locations || []).slice().sort(),
        statusFilter: (q.statusFilter || []).slice().sort(),
        perPage: this.perPage,
      };
      return JSON.stringify(norm);
    },
    hideKeyboard() {
      this.getJobs();
      this.$refs.searchInput.$refs.input.blur();
    },
    async rePrepareJob(data) {
      const jobIndex = this.updatedTableData.findIndex(
        (job) => job.id === data.jobId
      );
      const { jobObj } = await this.prepareJobObject(
        data.job,
        !this.computedIsOffline
      );

      this.$set(this.updatedTableData, jobIndex, jobObj);
    },
    openChatBox() {
      const lastSegment = this.$route.path.split('/').pop();

      const botNames = {
        editor: 'Eddit-i',
        publisher: 'Pubbl-i',
        inspector: 'Insp-i',
        report: 'Repair Select',
      };

      this.$chatbox.open({
        title: botNames[lastSegment],
        message: this.$t('template_list.how_can_i_help'),
      });
    },
    async saveJobOfflineWrapper(jobId) {
      const job = this.tableData.find((jobIt) => jobIt.id === jobId);
      if (job) job.isSavingOffline = true;

      await this.saveJobOffline(jobId);

      if (job) {
        job.isSavingOffline = false;
        job.offlineSavedIcon = true;
        job.hasCompleteData = true;
        job.checkedOut = this.user.id;
      }
    },

    async syncJobOnline(jobId) {
      await this.checkinJobOffline(jobId);
      this.tableData.filter((job) => {
        if (job.id === jobId) {
          job.offlineSavedIcon = false;
          job.needsSynchronization = false;
          delete job.checkedOut;
        }
      });
    },

    onQRDecode(data) {
      this.isQRCodeReaderVisible = false;
      // Decide the route based on "this.type"
      const routeName =
        this.type !== 'report' ? `${this.type}-tasks` : `${this.type}-template`;

      this.$store.commit(SET_LOADING_STATE, { value: true });
      // Finally, navigate
      this.$router.push({
        name: routeName,
        params: { id: data },
      });
    },

    async handleAddProductClick() {
      if (!this.canCreateJob) {
        return;
      }

      if (await this.preventOfflineAction()) {
        return;
      }

      this.$router.push(`/view/${this.type}/create`);
    },

    async refreshPageOnOnline() {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return;
      }

      if (this._listCancelled) {
        return;
      }

      await this.getJobs(true);
    },

    showSaveOffline(row) {
      return !row.checkedOut;
    },

    showSyncOnline(row) {
      if (row.offlineSavedIcon) return true;
      return false;
    },

    isJobCheckedOutCurrentUser(row) {
      return row.checkedOut && row.checkedOut === this.user.id;
    },

    getToolTipByRow(row) {
      let tooltip = '';

      if (
        row.checkedOut &&
        row.checkedOut !== this.user._id &&
        row.checkedOut !== this.user.id // Check also the copy of the id because for offline the user is taken from the session->user
      ) {
        tooltip = this.$t('template_list.checked_out_by', {
          user: row.checkedOut,
        });
      } else if (row.completenessPercentage) {
        tooltip += this.$t('template_list.completeness', {
          percentage: row.completenessPercentage,
        });
      }

      return tooltip;
    },

    getRowClass(row) {
      let rowClass = 'table-list-row';

      if (
        row.checkedOut &&
        row.checkedOut !== this.user._id &&
        row.checkedOut !== this.user.id // Check also the copy of the id because for offline the user is taken from the session->user
      ) {
        rowClass += ' dij-checked-out';
      }

      if (this.computedIsOffline && !row.hasCompleteData) {
        rowClass += ' dij-checked-out';
      }

      return rowClass;
    },

    filterFunc(row) {
      // eslint-disable-next-line no-restricted-syntax
      const keys = Object.keys(this.filterObject);
      if (keys.length > 0) {
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i];
          let value = row[key];

          if (value == null) return false;
          if (Number.isInteger(value)) {
            if (value !== Number(this.filterObject[key])) return false;
          } else {
            const re = new RegExp(this.filterObject[key], 'i');
            if (typeof value === 'boolean') value = `${value}`;
            if (!value.match(re)) return false;
          }
        }
        return true;
      }
    },
    canEditJob(job) {
      return this.$store.getters.canEditJob(job, this.type);
    },
    hasJobActions(job) {
      return (
        this.canEditJob(job) || (this.type === 'editor' && this.canCreateJob)
      );
    },
    checkSearchCookie() {
      // get search from session
      const value = sessionStorage.getItem('search');

      // if cookie has value and search is not defined, set search value
      if (value && !this.search) {
        this.search = value;
      }
    },
    async checkFilterCookie() {
      // get search cookie
      const value = await this.$cookies.get('templates_filter');
      // if cookie has value and search is not defined, set search value
      if (value && this.search && Object.keys(this.filterObject).length === 0) {
        this.filterObject = value;
      }

      this.cookieFilterChecked = true;
    },
    checkFavoriteCookie() {
      const isOn = this.$cookies.get('dij_show_favorites') === 'true';
      const hasFavList = !!(
        this.user.favoriteList && this.user.favoriteList.length
      );
      this.showFavorites = isOn && hasFavList;
    },
    saveFavoriteState() {
      if (this.showFavorites) {
        this.$cookies.set('dij_show_favorites', 'true');
      } else {
        this.$cookies.remove('dij_show_favorites');
      }
    },
    toggleFavoritesFilter() {
      this.showFavorites = !this.showFavorites;
      this.getJobs();
    },
    onStatusFilterChange(selectedStatuses) {
      this.statusFilter = Array.isArray(selectedStatuses)
        ? [...selectedStatuses]
        : [selectedStatuses].filter(Boolean);

      this.$cookies.set(
        `dij_status_filter_${this.type}`,
        this.statusFilter,
        60 * 60 * 24 * 7
      );
      this.getJobs();
    },
    onPageChange(page) {
      this.currentPage = page;

      if (this.search.length > 0) {
        console.log('Skipping get jobs');
      } else {
        this.getJobs();
      }

      if (this.type === 'editor') {
        sessionStorage.setItem('editor_page', this.currentPage);
      } else {
        sessionStorage.setItem('report_page', this.currentPage);
        sessionStorage.setItem('inspector_page', this.currentPage);
        sessionStorage.setItem('publisher_page', this.currentPage);
      }
    },
    createJobFetchingToast(message, type) {
      this.jobsFetchingToast = this.$buefy.toast.open({
        duration: 1000000,
        message: message || this.$t('template_list.retrieving_listings'),
        type: type || 'is-info',
        position: 'is-bottom',
      });
    },
    closeJobFetchingToast() {
      if (this.jobsFetchingToast) {
        this.jobsFetchingToast.close();
      }
    },
    updateJobFetchingMessage(message, type) {
      if (this.jobsFetchingToast) {
        this.jobsFetchingToast.message = message;
      } else {
        this.createJobFetchingToast(message, type);
        return;
      }

      if (type) {
        this.jobsFetchingToast.type = type;
      }
    },
    async getAllJobs(queryObj) {
      const currentQuery = ++this.queryCounter;

      const uniqueJobsMap = new Map();
      let lastKey;
      let batchCount = 0;

      this.createJobFetchingToast();

      this.$store.commit('SET_WAITING_FOR_READ', false);
      this.$store.commit('SET_PROGRESS_BAR', {
        key: 'list-loading',
        value: null,
        message: '',
      });
      try {
        let isNewQuery = true;
        do {
          if (this.queryCounter !== currentQuery) return { cancelled: true };

          const queryWithPagination = {
            ...queryObj,
            limitItems: 30,
            deepSearch: true,
            lastKey,
          };

          // Get previousRoute from localStorage
          const previousRoute = localStorage.getItem('previousRoute');
          const forceFetch = ['/', '/view/dashboard'].includes(previousRoute);
          if (forceFetch) {
            ['inspector_page', 'publisher_page', 'report_page'].forEach(
              (sessionStorageName) => {
                if (sessionStorage.getItem(sessionStorageName)) {
                  sessionStorage.removeItem(sessionStorageName);
                }
              }
            );
          }

          // eslint-disable-next-line no-await-in-loop
          const result = await this.$store.dispatch('getJobs', {
            ...queryWithPagination,
            keepLoading: true,
            forceFetch,
          });

          // Set isNewQuery from the first result
          console.log('isNewQuery:', isNewQuery);
          if (result && typeof result.isNewQuery !== 'undefined') {
            isNewQuery = result.isNewQuery;
            if (isNewQuery) {
              uniqueJobsMap.clear();
            }
          }

          if (this.queryCounter !== currentQuery) return { cancelled: true };

          if (Array.isArray(result.jobs)) {
            result.jobs.forEach((job) => uniqueJobsMap.set(job._id, job));
            batchCount++;

            this.updateJobFetchingMessage(
              this.$t('template_list.fetched_pages_found', {
                batchCount,
                size: uniqueJobsMap.size,
              })
            );
          }

          lastKey = result.lastKey;
        } while (lastKey);

        if (this.queryCounter !== currentQuery) {
          this.closeJobFetchingToast();
          return { cancelled: true };
        }

        const uniqueJobs = Array.from(uniqueJobsMap.values());
        this.updateJobFetchingMessage(
          this.$t('template_list.found_listings', { length: uniqueJobs.length })
        );
        this.$store.commit('SET_PROGRESS_BAR', {
          key: 'list-loading',
          value: null,
          message: '',
        });

        this.$store.commit('SET_JOBS', uniqueJobs);
        return { isNewQuery, allJobs: uniqueJobs };
      } catch (err) {
        if (this.queryCounter !== currentQuery) return { cancelled: true };

        console.error('Error fetching jobs:', err);
        this.updateJobFetchingMessage(
          this.$t('template_list.failed_to_fetch_listings'),
          'is-danger'
        );
        this.$store.commit('SET_PROGRESS_BAR', {
          key: 'list-loading',
          value: null,
          message: '',
        });
        setTimeout(() => {
          this.closeJobFetchingToast();
        }, 5000);
        return { isNewQuery: true, allJobs: [] };
      } finally {
        this.$store.commit('SET_WAITING_FOR_READ', false);
        setTimeout(() => {
          if (!this.waitingForRead) {
            this.closeJobFetchingToast();
          }
        }, 5000);
      }
    },

    // eslint-disable-next-line no-unused-vars
    async getJobs(forceRender = false, forceFirstPage = false) {
      this.closeJobFetchingToast();

      this._listCancelled = false;

      this.isSearchActive = this.search.length > 0;

      if (!this.isSearchActive) {
        this.$store.commit('SET_PROGRESS_BAR', {
          key: 'list-loading',
          value: 0,
          message: this.getListLoadingText(),
        });
      } else {
        this.$store.commit('SET_PROGRESS_BAR', {
          key: 'list-loading',
          value: null,
          message: '',
        });
      }

      if (!this.isSearchActive) {
        this.$refs.tableRef.filters = [];
        this.filterObject = {};
      }

      this.doneFetching = false;

      if (this.search) {
        sessionStorage.setItem('search', this.search);
      }

      const pagesAhead = 2;
      const expectedItems = this.perPage * pagesAhead;
      const expectedFetchedJobs =
        this.perPage * (this.currentPage + pagesAhead);

      const queryObj = {
        type: this.type,
        search: this.search.split(','),
        favorite: this.showFavorites,
        limitItems: expectedItems,
        expectedFetchedJobs,
        locations: [...this.selectedLocations],
        statusFilter: [...this.statusFilter],
      };

      if (this.type !== 'admin') {
        queryObj.favoriteList = this.user.favoriteList;
      }

      const newKey = this.buildQueryKey(queryObj);
      const isNewQueryLocal = newKey !== this.lastQueryKey;
      if (isNewQueryLocal) {
        this.lastQueryKey = newKey;
        this.currentPage = 1;
      }

      let result = null;
      if (this.isSearchActive) {
        result = await this.getAllJobs(queryObj);
        if (result.cancelled) return;
      } else {
        result = await this.$store.dispatch('getJobs', queryObj);
      }

      if (!result) {
        this.$store.commit('SET_PROGRESS_BAR', {
          key: 'list-loading',
          value: null,
          message: '',
        });
        this.$store.commit('SET_WAITING_FOR_READ', false);
        if (this.showFavorites) {
          this.showFavorites = false;
        }
        this.saveFavoriteState();
        this.$buefy.toast.open({
          message: this.$t('template_list.failed_to_fetch_listings'),
          type: 'is-danger',
          position: 'is-bottom',
          duration: 5000,
        });
        return;
      }

      this.saveFavoriteState();

      const isNewQuery = isNewQueryLocal || !!result.isNewQuery;

      let startIndex;
      if (isNewQuery || forceRender) {
        this.tableData = [];
        startIndex = 0;
        this.currentPage = 1;
      } else {
        startIndex = this.tableData.length;
      }

      const isOnline = !this.computedIsOffline;

      const totalToProcess = Math.max(1, this.jobs.length - startIndex);
      let processedCount = 0;

      for (let i = startIndex; i < this.jobs.length; i++) {
        const job = this.jobs[i];
        // eslint-disable-next-line no-continue
        if (!job) continue;

        // eslint-disable-next-line no-await-in-loop
        const { jobObj } = await this.prepareJobObject(job, isOnline);

        if (this._listCancelled) break;

        this.tableData.push(jobObj);

        if (!this.isSearchActive) {
          processedCount += 1;
          const progress = Math.min(
            95,
            Math.round((processedCount / totalToProcess) * 95)
          );
          this.$store.commit('SET_PROGRESS_BAR', {
            key: 'list-loading',
            value: progress,
            message: this.getListLoadingText(processedCount, totalToProcess),
          });
        }
      }

      // dedup by id (safe guard)
      this.tableData = Array.from(
        new Map(this.tableData.map((job) => [job.id, job])).values()
      );

      const element = document.querySelector('nav.pagination small.info.more');
      if (result.hasMoreData) {
        element.innerText = '+';
      } else {
        element.innerText = '';
      }

      if (!result.isNewQuery) {
        const cookiePage =
          parseInt(sessionStorage.getItem(`${this.type}_page`), 10) || 1;
        this.currentPage = cookiePage;
      }

      this.doneFetching = true;
      this.$store.commit('SET_WAITING_FOR_READ', false);

      if (!this.isSearchActive) {
        this.$store.commit('SET_PROGRESS_BAR', {
          key: 'list-loading',
          value: 100,
          message: this.getListLoadedText(),
        });
        setTimeout(() => {
          this.$store.commit('SET_PROGRESS_BAR', {
            key: 'list-loading',
            value: null,
          });
        }, 1600);
      }
    },

    async prepareJobObject(job, isOnline) {
      const thumbnailUrl = this.getThumbnail(job);

      const jobObj = {
        id: job._id,
        prodCreatedByUser: job.prodCreatedByUser,
        nameId: job.name,
        picture: thumbnailUrl,
        title: job.title,
        product_line: this.getAndFormatDropValues(job.workOrderName),
        original_oem: this.getAndFormatDropValues(job.brand),
        frame_type: this.getAndFormatDropValues(job.frameType),
        model: this.getAndFormatDropValues(job.model),
        part_number: job.userEnteredPartNumber,
        executing_site: this.getExecutingSiteName(job),
        location: this.getJobLocationName(job),
        document_number: job.documentNumber,
        revision: job.revision,
        created_by: this.getCreatedBy(job),
        date: this.getDateString(job),
        completenessClass: this.getCompleteness(job),
        completenessValueFiltered: job.jobCompleted
          ? 0
          : 100 - parseInt(job.completenessPercentage, 10),
        completenessPercentage: job.jobCompleted
          ? '100%'
          : `${job.completenessPercentage}` || '0%',
        favoriteCount: job.favoriteCount || 0,
        customer: job.customerInfoText,
        project_number: job.projectInfoText,
        serial_number: job.serialNumber,
        work_order_number: job.workOrderNumber,
        sales_force_opportunity_number: job.salesForceNumber,
        oldOwners: job.oldOwners || [],
        sapReference: job.sapReference,
        owner_date: this.getJobOwnerDate(job),
        includesRepairAssessment:
          !!job.repairAssessment && job.repairAssessment.length !== 0,
        checkedOut: job.checkedOut || null,
        checkedOutAt: job.checkedOutAt || null,
        CSM_Repair_Engineer: `${job?.csmUser?.[0]?.name || ''}${
          job?.csmUser?.[0]?.name && job?.repairEngineerUser?.[0]?.name
            ? ' / '
            : ''
        }${job?.repairEngineerUser?.[0]?.name || ''}`,
        status: getEffectiveJobStatus(job, this.type),
      };

      jobObj.offlineSavedIcon = this.isJobCheckedOutCurrentUser(jobObj);

      const jobTasks = await getRecordsUsingIndex('task', 'jobId', job._id);
      jobObj.hasCompleteData = jobTasks.length > 0;

      if (isOnline) {
        jobObj.needsSynchronization = await this.doesJobNeedsSynchronization(
          jobObj
        );
      }

      return { jobObj };
    },
    async doesJobNeedsSynchronization(jobObj) {
      if (!jobObj.offlineSavedIcon) {
        return false;
      }

      const dirtySyncRecords = await getDirtySyncRecordsByJobId(jobObj.id);

      let hasChanges = false;
      dirtySyncRecords.forEach((record) => {
        if (record.checkedJobId) {
          hasChanges = true;
        }
      });

      return hasChanges;
    },
    getJobOwnerDate(job) {
      if (!job.prodCreatedByUser) {
        return 'N/A';
      }
      return `${job.prodCreatedByUser.fullname} / ${this.getDateString(job)}`;
    },
    getJobLocationName(job) {
      if (job.prodCreatedByUser && job.prodCreatedByUser.location) {
        const { location } = job.prodCreatedByUser;
        if (typeof location === 'object') {
          return job.prodCreatedByUser.location.siteName;
        }
        return this.$store.getters.siteName(location);
      }
      return '';
    },
    getExecutingSiteName(job) {
      if (job.executingSite) {
        if (typeof job.executingSite === 'object') {
          return job.executingSite.siteName || 'N/A';
        }

        const foundSite = this.sites.find(
          (site) => site._id === job.executingSite
        );
        if (foundSite) {
          return foundSite.siteName;
        }

        return String(job.executingSite);
      }
      return 'N/A';
    },
    getCreatedBy(job) {
      if (job.prodCreatedByUser && job.prodCreatedByUser.fullname) {
        return job.prodCreatedByUser.fullname;
      }
      if (job.modifier && job.modifier.fullname) {
        return job.modifier.fullname;
      }
      return '';
    },
    getDateString(job) {
      let { date } = job;
      const { prodCreatedByUser } = job;
      if (!date && prodCreatedByUser) {
        date = prodCreatedByUser.timestamp;
      }
      if (
        !date ||
        (typeof date !== 'number' && Object.keys(date).length === 0)
      ) {
        date = new Date().getTime();
      }
      const dateObject = new Date(parseFloat(date));
      const dateString = `${
        dateObject.getMonth() + 1
      }-${dateObject.getDate()}-${dateObject.getFullYear()}`;

      return dateString;
    },
    getCompleteness(job) {
      const completeness = job.completenessPercentage;
      if (job.jobCompleted) {
        return 'completeness-done';
      }
      if (parseInt(completeness, 10) <= 100 && !job.jobCompleted) {
        return 'completeness-progress';
      }
      return 'completeness-default';
    },
    getThumbnail(job) {
      let url;
      if (!job.thumbnailUrl) {
        job.thumbnailUrl = '/image-empty';
        url = `${window.location.origin}/img/image-empty.png`;
      } else if (job.thumbnailUrl.includes('/image-empty')) {
        url = `${window.location.origin}/img/image-empty.png`;
      } else if (job.thumbnailUrl.startsWith('http')) {
        url = job.thumbnailUrl; // Already absolute
      } else {
        if (!job.thumbnailUrl) {
          console.log('No thumbnail URL available');
          return null;
        }
        url = `${window.location.origin}${job.thumbnailUrl}`;
      }
      // For local env, modify URL if valid
      if (job.thumbnailUrl && window.location.host.includes('localhost')) {
        url = `http://localhost:4566/images${job.thumbnailUrl}`;
      }
      return url;
    },
    getAndFormatDropValues(dropType) {
      let namesFormated = [];
      if (typeof dropType === 'undefined' || dropType === null) return '';
      if (typeof dropType !== 'string' && dropType.length > 1) {
        dropType.forEach((type) => {
          namesFormated.push(type.text);
        });
      } else if (typeof dropType === 'string') {
        namesFormated = dropType;
      } else if (typeof dropType[0] !== 'undefined') {
        namesFormated = dropType[0].text;
      } else {
        namesFormated = '';
      }
      if (typeof namesFormated === 'string') {
        return namesFormated;
      }
      if (Array.isArray(namesFormated)) {
        return namesFormated.join(', ');
      }
      return '';
    },
    goToJob(row) {
      this.closeJobFetchingToast();

      if (
        row.checkedOut &&
        row.checkedOut !== this.user._id &&
        row.checkedOut !== this.user.id // Check also the copy of the id because for offline the user is taken from the session->user
      ) {
        return;
      }

      const { id } = row;

      // Basic validation checks
      if (!id) {
        console.error('goToJob called without a valid "id".');
        return;
      }
      if (!this.type) {
        console.error('goToJob called but "this.type" is not set.');
        return;
      }

      // Schedule navigation on the next event loop cycle
      setTimeout(() => {
        // If the user clicked "options," reset and stop here
        if (this.optionsClicked) {
          this.optionsClicked = false;
          return false;
        }
        // Decide the route based on "this.type"
        const routeName =
          this.type !== 'report'
            ? `${this.type}-tasks`
            : `${this.type}-template`;

        this._listCancelled = true;
        this.$store.commit('CANCEL_PROGRESS_TASK', { key: 'list-loading' });
        this.$store.commit('SET_PROGRESS_BAR', {
          key: 'job-loading',
          value: 0,
          message: this.$t('template_list.loading_job'),
        });

        this.$store.commit(SET_LOADING_STATE, { value: true });
        // Finally, navigate
        this.$router.push({
          name: routeName,
          params: { id },
        });
      }, 0);
    },

    setFavoriteCount(id, newCount) {
      const currentJob = this.jobs.find((data) => data._id === id);
      if (currentJob) {
        this.$store.commit(SET_FAVOURITE_CURRENT_JOB, currentJob);
        this.$store.commit(UPDATE_FAVORITE_COUNT, {
          id,
          newCount,
        });
      }
      const job = this.tableData.find((data) => data.id === id);
      if (job) {
        job.favoriteCount = newCount;
      }
    },

    // A single click toggles the desired state, updates the UI optimistically,
    // and debounces a network sync. Rapid mashing collapses into one converging
    // request instead of N concurrent ones that can resolve out of order.
    addToFavorites(id, nameId, count) {
      this.optionsClicked = true;

      const isFavorited = !!(
        this.user.favoriteList && this.user.favoriteList.includes(id)
      );

      if (!this.favoriteSync[id]) {
        this.favoriteSync[id] = {
          desired: isFavorited,
          baseFavorited: isFavorited,
          baseCount: count,
          inFlight: false,
          needsResync: false,
        };
      }
      const entry = this.favoriteSync[id];

      // Block only when turning a favorite ON would exceed the cap.
      const willTurnOn = !entry.desired;
      if (
        willTurnOn &&
        !isFavorited &&
        this.user.favoriteList &&
        this.user.favoriteList.length >= 140
      ) {
        this.$buefy.snackbar.open({
          message: this.$t('template_list.max_favorites_warning'),
          type: 'is-warning',
          position: 'is-top',
          pauseOnHover: true,
          duration: 1000,
        });
        return;
      }

      entry.desired = !entry.desired;
      this.applyOptimisticFavorite(id, entry);

      if (this.favoriteTimers[id]) {
        clearTimeout(this.favoriteTimers[id]);
      }
      this.favoriteTimers[id] = setTimeout(() => {
        this.favoriteTimers[id] = null;
        this.syncFavorite(id, nameId);
      }, 450);
    },

    // Reflect the desired state in the star (favoriteList) and the counter
    // immediately, derived from the confirmed baseline so the number stays
    // stable no matter how many times the user clicks.
    applyOptimisticFavorite(id, entry) {
      const list = this.user.favoriteList || [];
      const idx = list.indexOf(id);
      if (entry.desired && idx === -1) {
        list.push(id);
      } else if (!entry.desired && idx > -1) {
        list.splice(idx, 1);
      }

      let delta = 0;
      if (entry.desired !== entry.baseFavorited) {
        delta = entry.desired ? 1 : -1;
      }
      this.setFavoriteCount(id, Math.max(0, entry.baseCount + delta));
    },

    // Send at most one request per job at a time. If the user keeps toggling
    // while a request is in flight, re-sync once it resolves so we converge to
    // the final desired state with the server's authoritative count.
    async syncFavorite(id, nameId) {
      const entry = this.favoriteSync[id];
      if (!entry || entry.inFlight) {
        if (entry) {
          entry.needsResync = true;
        }
        return;
      }
      if (entry.desired === entry.baseFavorited) {
        return;
      }

      entry.inFlight = true;
      const desiredAtSend = entry.desired;
      const action = desiredAtSend
        ? 'addFavoriteTemplate'
        : 'removeFavoriteTemplate';

      try {
        const result = await this.$store.dispatch(action, {
          id,
          nameId,
          count: entry.baseCount,
        });
        let authoritative;
        if (result && result.changed === false) {
          authoritative = entry.baseCount;
        } else if (
          result &&
          typeof result.count === 'number' &&
          result.count >= 0
        ) {
          authoritative = result.count;
        } else {
          authoritative = Math.max(
            0,
            entry.baseCount + (desiredAtSend ? 1 : -1)
          );
        }

        entry.baseFavorited = desiredAtSend;
        entry.baseCount = authoritative;
        this.setFavoriteCount(id, authoritative);
      } catch (error) {
        this.revertFavorite(id, entry);
      } finally {
        entry.inFlight = false;
        if (entry.needsResync) {
          entry.needsResync = false;
          if (entry.desired !== entry.baseFavorited) {
            this.syncFavorite(id, nameId);
          }
        }
      }
    },

    // Roll the UI back to the last server-confirmed state after a failed sync.
    revertFavorite(id, entry) {
      entry.desired = entry.baseFavorited;
      const list = this.user.favoriteList || [];
      const idx = list.indexOf(id);
      if (entry.baseFavorited && idx === -1) {
        list.push(id);
      } else if (!entry.baseFavorited && idx > -1) {
        list.splice(idx, 1);
      }
      this.setFavoriteCount(id, entry.baseCount);
    },
    favoriteStarClass(id) {
      if (
        this.user?.favoriteList?.length > 0 &&
        this.user.favoriteList.includes(id)
      ) {
        return 'favorite-star active';
      }
      return 'favorite-star';
    },
    async duplicate(id) {
      const index = this.jobs.findIndex((job) => job._id === id);
      if (index < 0) return false;

      const job = this.jobs[index];
      if (!this.canEditJob(job)) {
        return false;
      }

      await this.$store.dispatch('duplicateJob', job);
      return this.getJobs(true);
    },
    async deleteJob(id, title) {
      if (await this.preventOfflineAction()) {
        return;
      }

      const index = this.jobs.findIndex((job) => job._id === id);
      if (index < 0 || !this.canEditJob(this.jobs[index])) {
        return false;
      }

      this.$buefy.dialog.confirm({
        title: this.$t('template_list.delete_confirmation_title'),
        message: this.$t('template_list.delete_confirmation_message', {
          title,
        }),
        confirmText: this.$t('template_list.delete_confirm_button'),
        type: 'is-dark',
        hasIcon: true,
        icon: 'delete',
        onConfirm: async () => {
          this.isLoading = true;
          await this.$store.dispatch('deleteJob', id);
          await this.getJobs(true);
          await this.$store.dispatch('getDeletedJobs', { type: this.type });

          this.isLoading = false;
        },
      });
    },
    async copyFrom(id) {
      if (await this.preventOfflineAction()) {
        return;
      }
      const jobIndex = this.jobs.findIndex((item) => item._id === id);
      if (jobIndex < 0) return;

      const job = { ...this.jobs[jobIndex] };
      if (!this.canEditJob(job)) {
        return;
      }

      if (this.type === 'editor') {
        this.$store.commit('SET_CURRENT_JOB', job);
        this.$router.push({ name: 'editor-copyfrom', params: { id } });
      } else {
        this.$store.commit('SET_CURRENT_JOB', job);
        this.$router.push({ name: 'publisher-copyfrom', params: { id } });
      }
    },
    async editJob(id) {
      if (await this.preventOfflineAction()) {
        return;
      }
      const jobIndex = this.jobs.findIndex((item) => item._id === id);
      if (jobIndex < 0) return;

      const job = { ...this.jobs[jobIndex] };
      if (!this.canEditJob(job)) {
        return;
      }

      if (this.type === 'editor') {
        this.$store.commit('SET_CURRENT_JOB', job);
        this.$router.push({ name: 'editor-edit-job', params: { id } });
      } else {
        this.$store.commit('SET_CURRENT_JOB', job);
        this.$router.push({ name: 'publisher-edit-order', params: { id } });
      }
    },
    formatColumnName(columnName) {
      if (this.$te(columnName)) {
        return this.$t(columnName);
      }
      const words = columnName.split('_');
      words.forEach((word, index) => {
        words[index] =
          words[index].charAt(0).toUpperCase() + words[index].slice(1);
      });
      return words.join(' ');
    },

    clearFilters() {
      this.filterObject = {};
      this.$cookies.remove('templates_filter');
    },
    async updateLocation(newListLocations) {
      this.selectedLocations = newListLocations;

      await this.getJobs();
    },
    showDeletedJobs() {
      this.isDeletedJobsVisible = true;
    },
    hideDeletedJobs() {
      this.isDeletedJobsVisible = false;
    },
    async undelete(jobId) {
      this.jobsUndeleted = true;

      await this.$store.dispatch('undeleteJob', jobId);
    },
    convertEpochToDate(epoch) {
      const date = new Date();
      date.setTime(epoch * 1000);
      return date
        .toISOString()
        .slice(0, 19)
        .replace(/-/g, '/')
        .replace('T', ' ');
    },
  },
};
</script>
<style lang="scss" scoped>
.floating-button {
  color: #7957d5;
  position: fixed;
  width: 60px;
  height: 60px;
  bottom: 100px; /* Bottom margin */
  right: 100px; /* Right margin */
  z-index: 1000; /* Ensure it floats above other components */
}

.floating-button:hover .tooltip {
  opacity: 1; /* Show tooltip on hover */
}

.header {
  font-family: 'Siemens Sans';
  display: inline-flex;
  justify-content: space-between;
  position: relative;
  width: 100%;
  height: 50px;
  background-color: #fafafa;
  font-size: 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  color: #333;
  padding-left: 20px;
  .right-section {
    float: right;
    display: inline-flex;
    .search {
      height: 100%;
      padding: 5px 20px;
      background-color: #e0e0e0;
      .search-input input {
        border: 0px !important;
        background-color: #e0e0e0 !important;
        outline: none;
        box-shadow: none;
      }
    }
  }
  .list-title {
    height: 50px;
    line-height: 50px;
    padding: 0 10px;
  }
}
.items {
  height: calc(100vh - 100px) !important;
  overflow-y: auto;
  .items-container {
    padding: 45px 30px 10px 30px;
  }
}
img.thumbnail {
  width: 130px;
  min-width: 130px;
  height: 60px;
}
.completeness-default,
.completeness-done,
.completeness-progress {
  border-left: solid 8px;
  position: relative;
  .completeness-inner {
    content: '';
    height: 0%; //You can change this if you want smaller/bigger borders
    width: 8px;
    position: absolute;
    left: -8px;
    top: 0; // If you want to set a smaller height and center it, change this value
    background-color: #aaa; // The color of your border
  }
}
.completeness-default {
  border-left-color: #aaa;
}
.completeness-done {
  border-left-color: rgb(123, 249, 109);
}
.completeness-progress {
  border-left-color: #fcb95b;
}
.favorite-star {
  color: #ccc;
  margin: 2px;
  cursor: pointer;
  &:hover {
    color: #ffdd00;
  }
  &.active {
    color: #ffdd00;
    &:hover {
      color: #ccc;
    }
  }
}

.favorite-filter-icon {
  display: flex;
  align-items: center;
  height: 50px;
  padding: 0 12px;
  cursor: pointer;
  color: #ccc;
  &:hover {
    color: #ffdd00;
  }
  &.active {
    color: #ffdd00;
  }
}
.options-column {
  cursor: default;
  min-width: 120px;
  div {
    display: inline-block;
    margin: 0 3px;
    line-height: 2.5;
    button {
      color: #ccc;
      &:hover {
        color: black;
      }
      &:active {
        color: blue;
      }
      &:focus {
        box-shadow: none;
      }
    }
  }
  .options-more-btn {
    border: none;
  }
}

.button-wrapper {
  display: flex;
}

.flex-fill {
  flex: 1 1 auto;
  height: 100%;
  width: 50px;
}

.text-column-limit {
  line-height: 20px;
  word-break: break-all;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -moz-box-orient: vertical;
  -ms-box-orient: vertical;
  box-orient: vertical;
  -webkit-line-clamp: 5;
  -moz-line-clamp: 5;
  -ms-line-clamp: 5;
  line-clamp: 5;
  overflow: hidden;
  transition: 0s overflow;
  transition: 0s display;

  &:hover {
    width: 300px;
    overflow: visible;
    display: table;

    transition-delay: 1s;
  }
}

.image-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.image-overlay {
  position: absolute;
  top: 0;
  right: 0;
  opacity: 0.7;
}

.image-overlay-offline {
  position: absolute;
  top: 0;
  left: 0;
  background-color: #fff;
  background-color: rgba(255, 255, 255, 0.4);
}

$tooltip-multiline-sizes: (
  small: 180px,
  medium: 240px,
  large: 400px,
);
$tooltip-colors: blue;

.filter-location {
  height: 100%;
  display: inline-block;
}

.filter-status {
  height: 100%;
  display: inline-block;
}

.dij-jobs-recycle-bin.button.is-success {
  border-left: 1px solid;
  height: 50px;
  padding-top: 0;
  padding-bottom: 0;
}

.dij-jobs-deleted-modal .modal-card {
  width: 960px;

  .modal-card-body {
    font-size: 16px;
  }
}

.dij-status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.dij-status-draft {
  background-color: #e8e8e8;
  color: #555;
}

.dij-status-in-progress {
  background-color: #d4edda;
  color: #155724;
}

.dij-status-closed {
  background-color: #f8d7da;
  color: #721c24;
}

.dij-status-active {
  background-color: #cce5ff;
  color: #004085;
}
</style>

<style lang="scss">
.dij-checked-out {
  background-color: #f5f5f5;
  color: #b0b0b0;
  opacity: 0.8;
}

.loading-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.loading-text {
  margin-top: 3.75rem;
}
.qr-reader-content {
  background: white;
  border-radius: 10px;
  padding: 20px;
  gap: 10px;
  display: flex;
  flex-direction: column;

  justify-content: space-between;
}
</style>
